import { UpdateAppointmentRequest } from "@/data/dtos/requests";
import { UpdateAppointmentRequestSchema } from "@/data/schemas";
import { ApiQuery, ApiService } from "@/domain/clients";
import {
  AppointmentStatus,
  RecurrenciesVerification,
} from "@/domain/contracts";
import {
  CreateAppointmentInput,
  CreateRescheduleRequestInput,
  UpdateReceiptInput,
} from "@/domain/dtos/inputs";
import { RecurrenciesVerificationParams } from "@/domain/dtos/params";
import { NotFoundError } from "@/domain/errors";
import { Endpoints } from "@/infra/api";
import { ValidationUtils } from "@/infra/utils/validation";
import {
  AppointmentRecurrencyResponse,
  AppointmentResponse,
} from "@/data/dtos/responses";
import { AppointmentDataSource } from "./AppointmentDataSource";

export class AppointmentRemoteDataSource implements AppointmentDataSource {
  constructor(private readonly api: ApiService) {}

  async getAppointments(
    query: ApiQuery & {
      psychologist_id?: number;
      status?: AppointmentStatus[];
    },
  ): Promise<AppointmentResponse[]> {
    const { data } = await this.api.get<AppointmentResponse[]>(
      Endpoints.appointments,
      query,
    );

    if (!data) {
      throw new NotFoundError("Sessões");
    }

    return data;
  }

  async getAppointmentById(id: number): Promise<AppointmentResponse> {
    const { data } = await this.api.get<AppointmentResponse>(
      `${Endpoints.appointments}/${id}`,
    );

    if (!data) {
      throw new NotFoundError("Sessão");
    }

    return data;
  }

  async createAppointment(
    params: CreateAppointmentInput,
  ): Promise<AppointmentResponse> {
    const { data } = await this.api.post<
      AppointmentResponse,
      CreateAppointmentInput
    >(Endpoints.appointments, params);

    if (!data) {
      throw new NotFoundError("Sessão");
    }

    return data;
  }

  async updateAppointment(
    id: number,
    params: UpdateAppointmentRequest,
  ): Promise<AppointmentResponse> {
    ValidationUtils.validate(UpdateAppointmentRequestSchema, params);
    const { data } = await this.api.put<
      AppointmentResponse,
      UpdateAppointmentRequest
    >(`${Endpoints.appointments}/${id}`, params);

    if (!data) {
      throw new NotFoundError("Sessão");
    }

    return data;
  }

  async getRecurrencyById(id: number): Promise<AppointmentRecurrencyResponse> {
    const { data } = await this.api.get<AppointmentRecurrencyResponse>(
      `${Endpoints.appointment_recurrencies}/${id}`,
    );

    if (!data) {
      throw new NotFoundError("Recorrência");
    }

    return data;
  }

  async cancelRecurrency(id: number): Promise<void> {
    await this.api.delete(`${Endpoints.appointment_recurrencies}/${id}`);
  }

  async updateReceipt(
    id: number,
    params: UpdateReceiptInput,
  ): Promise<{ message: string }> {
    const { message } = await this.api.post(
      `${Endpoints.appointments}/${id}/edit`,
      params,
      {
        encodeType: "formData",
      },
    );

    return {
      message:
        message ||
        "Recibo foi anexado e você pode visualizar na tela de detalhes.",
    };
  }

  async recurrenciesVerification(
    params: RecurrenciesVerificationParams,
  ): Promise<RecurrenciesVerification> {
    const { data } = await this.api.post<
      RecurrenciesVerification,
      RecurrenciesVerificationParams
    >(`${Endpoints.appointment_recurrencies}/verification`, params);

    if (!data) {
      throw new Error("Erro ao verificar disponibilidade de agenda.");
    }

    return data;
  }

  async updateRescheduleRequest(
    id: number,
    params: { status: string },
  ): Promise<any> {
    return this.api.put(`${Endpoints.reschedule_requests}/${id}`, params);
  }

  async createRescheduleRequest(
    params: CreateRescheduleRequestInput,
  ): Promise<any> {
    return this.api.post(Endpoints.reschedule_requests, params);
  }

  createCallEvent(params: {
    appointment_id: number;
    event: string;
  }): Promise<any> {
    return this.api.post(Endpoints.call_events, params);
  }
}
